import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, ArrowUpRight, Clock, Languages } from 'lucide-react'
import { useT, useLanguage } from '../i18n/LanguageContext'
import { useDocumentMeta } from '../hooks/useDocumentMeta'
import { getPost, getPosts, formatDate } from '../content/blog'
import PostCard from '../components/blog/PostCard'

export default function BlogPost() {
  const { slug = '' } = useParams()
  const t = useT()
  const { language } = useLanguage()

  const post = useMemo(() => getPost(slug, language), [slug, language])

  // Dwa kolejne artykuły do dalszej lektury — z pominięciem tego czytanego.
  const more = useMemo(
    () => getPosts(language).filter((p) => p.slug !== slug).slice(0, 2),
    [language, slug]
  )

  useDocumentMeta(
    post ? `${post.title} | Solid Labs PSA` : t.blog.notFound.title,
    post?.lead || t.blog.meta.description
  )

  if (!post) {
    return (
      <section className="relative pt-40 pb-28 min-h-screen">
        <div className="absolute inset-0 bg-radial-top" aria-hidden="true" />
        <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
          <h1 className="font-display text-3xl sm:text-4xl font-light text-white tracking-tight mb-5">
            {t.blog.notFound.title}
          </h1>
          <p className="text-zinc-400 leading-relaxed mb-10">
            {t.blog.notFound.description}
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-700 hover:bg-brand-600 text-white font-medium rounded-lg border border-white/[0.08] hover:border-white/[0.14] transition-all"
          >
            <ArrowLeft size={18} aria-hidden="true" />
            {t.blog.notFound.cta}
          </Link>
        </div>
      </section>
    )
  }

  const isFallbackLanguage = post.language !== language

  return (
    <>
      <article className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-radial-top" aria-hidden="true" />

        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              to="/blog"
              className="group inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-300 transition-colors mb-12"
            >
              <ArrowLeft
                size={15}
                aria-hidden="true"
                className="group-hover:-translate-x-0.5 transition-transform"
              />
              {t.blog.backToList}
            </Link>

            <header className="mb-12">
              {post.category && (
                <p className="font-mono text-accent-400 text-xs tracking-[0.2em] uppercase mb-5">
                  {post.category}
                </p>
              )}

              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight leading-[1.1] mb-6">
                {post.title}
              </h1>

              {post.lead && (
                <p className="text-lg sm:text-xl text-zinc-400 leading-relaxed mb-8">
                  {post.lead}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-zinc-500 pb-8 border-b border-white/[0.08]">
                <span className="text-zinc-300">{post.author}</span>
                <span aria-hidden="true" className="text-zinc-700">·</span>
                <time dateTime={post.date} className="font-mono text-xs">
                  {formatDate(post.date, post.language)}
                </time>
                <span aria-hidden="true" className="text-zinc-700">·</span>
                <span className="inline-flex items-center gap-1.5 text-xs">
                  <Clock size={13} aria-hidden="true" />
                  {post.readingMinutes} {t.blog.readingTime}
                </span>
              </div>
            </header>

            {isFallbackLanguage && (
              <p
                lang={post.language}
                className="flex items-start gap-3 mb-10 px-5 py-4 rounded-xl bg-brand-500/[0.08] border border-brand-500/[0.22] text-sm text-zinc-300"
              >
                <Languages size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-brand-400" />
                {t.blog.translationNotice}
              </p>
            )}

            {post.cover && (
              <img
                src={post.cover}
                alt=""
                aria-hidden="true"
                className="w-full rounded-2xl border border-white/[0.08] mb-12"
              />
            )}

            {/*
              Treść pochodzi z naszych plików .md w repozytorium — nie z pola
              formularza ani z API — więc wstrzyknięcie HTML jest tu bezpieczne.
              Gdyby kiedyś artykuły miały przychodzić z CMS-a, trzeba dołożyć
              sanityzację (DOMPurify) przed tym miejscem.
            */}
            <div
              lang={post.language}
              className="prose-article"
              dangerouslySetInnerHTML={{ __html: post.html }}
            />
          </motion.div>
        </div>
      </article>

      <section className="relative pb-28">
        <div className="max-w-3xl mx-auto px-6">
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] px-8 py-10 text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-light text-white tracking-tight mb-4">
              {t.blog.articleCta.title}
            </h2>
            <p className="text-zinc-400 leading-relaxed max-w-xl mx-auto mb-8">
              {t.blog.articleCta.subtitle}
            </p>
            <a
              href="mailto:hello@solidlabs.site"
              className="group inline-flex items-center gap-2 px-7 py-3.5 bg-brand-700 hover:bg-brand-600 text-white font-medium rounded-lg border border-white/[0.08] hover:border-white/[0.14] transition-all"
            >
              {t.blog.articleCta.button}
              <ArrowUpRight
                size={18}
                aria-hidden="true"
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
              />
            </a>
          </div>

          {more.length > 0 && (
            <div className="mt-20">
              <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-6">
                {t.blog.moreArticles}
              </h2>
              <div className="grid sm:grid-cols-2 gap-6">
                {more.map((next) => (
                  <PostCard key={next.slug} post={next} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  )
}
